import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Mail, Phone } from "lucide-react";
import { ChapterTag, MaskedLine, Reveal } from "@/components/bits";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONFIG_OPTIONS = ["Studio", "2 BHK", "2.5 BHK", "3 BHK", "3.5 BHK"];

const inputCls =
  "w-full border-b border-[#F5F5F4]/25 bg-transparent py-3.5 text-sm text-[#FAF9F6] placeholder-[#F5F5F4]/40 outline-none transition-colors duration-300 focus:border-[#A98B5F]";

export default function Enquiry() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", configuration: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, {
        ...form,
        configuration: form.configuration || null,
        message: form.message || null,
      });
      setDone(true);
      toast.success("Thank you — our team will reach out shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please call us at 88800 50000.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="enquire" className="grain relative bg-[#022C22] py-24 md:py-36" data-testid="enquiry-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-20 px-6 md:px-12 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <ChapterTag num="07" label="Get in Touch" light />
          <h2 className="font-display mt-12 text-4xl font-light leading-[1.05] tracking-tight text-[#FAF9F6] sm:text-5xl lg:text-6xl">
            <MaskedLine inView delay={0.05}>Welcome home to</MaskedLine>
            <MaskedLine inView delay={0.15}>
              a life <span className="italic text-[#A98B5F]">in bloom.</span>
            </MaskedLine>
          </h2>
          <Reveal delay={0.3} className="mt-10 max-w-md text-sm leading-relaxed text-[#F5F5F4]/65 md:text-base">
            <p>
              Register your interest and our team will share pricing, floor plans, and a
              private walkthrough of the experience centre.
            </p>
          </Reveal>
          <Reveal delay={0.4} className="mt-12 space-y-6">
            <a
              href="tel:+918880050000"
              data-testid="contact-phone-link"
              className="group flex items-center gap-5 text-[#FAF9F6]"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-full border border-[#F5F5F4]/25 transition-colors duration-300 group-hover:border-[#A98B5F]">
                <Phone size={16} />
              </span>
              <span className="font-display text-2xl font-light tracking-wide">+91 88800 50000</span>
            </a>
            <a
              href="mailto:sales@embassysouthreserve.com"
              data-testid="contact-email-link"
              className="group flex items-center gap-5 text-[#FAF9F6]"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-full border border-[#F5F5F4]/25 transition-colors duration-300 group-hover:border-[#A98B5F]">
                <Mail size={16} />
              </span>
              <span className="text-sm tracking-wide text-[#F5F5F4]/80">sales@embassysouthreserve.com</span>
            </a>
          </Reveal>
        </div>

        <div className="lg:col-span-7 lg:pl-10">
          <Reveal delay={0.25}>
            {done ? (
              <div
                className="flex h-full min-h-[26rem] flex-col items-center justify-center rounded-sm border border-[#F5F5F4]/15 p-12 text-center"
                data-testid="enquiry-success"
              >
                <CheckCircle2 size={40} className="text-[#A98B5F]" />
                <h3 className="font-display mt-8 text-3xl font-light text-[#FAF9F6] md:text-4xl">
                  Thank you, {form.name.split(" ")[0]}.
                </h3>
                <p className="mt-4 max-w-sm text-sm leading-relaxed text-[#F5F5F4]/65">
                  Your enquiry for Embassy South Reserve has been received. Our team will
                  contact you on {form.phone} shortly.
                </p>
                <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
                  <a
                    href="/brochure.pdf"
                    download="Embassy-South-Reserve-Brochure.pdf"
                    data-testid="brochure-download-btn"
                    className="rounded-full bg-[#A98B5F] px-8 py-3 text-xs font-semibold uppercase tracking-[0.22em] text-[#022C22] transition-transform duration-300 hover:scale-105"
                  >
                    Download Brochure
                  </a>
                  <button
                    onClick={() => { setDone(false); setForm({ name: "", phone: "", email: "", configuration: "", message: "" }); }}
                    data-testid="enquiry-another-btn"
                    className="rounded-full border border-[#F5F5F4]/30 px-8 py-3 text-xs font-semibold uppercase tracking-[0.22em] text-[#FAF9F6] transition-colors duration-300 hover:border-[#A98B5F] hover:text-[#A98B5F]"
                  >
                    Send another enquiry
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="rounded-sm border border-[#F5F5F4]/15 p-8 md:p-12" data-testid="enquiry-form">
                <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
                  <div>
                    <label className="text-[10px] font-semibold uppercase tracking-[0.26em] text-[#F5F5F4]/50">Full Name</label>
                    <input required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} data-testid="enquiry-name-input" />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold uppercase tracking-[0.26em] text-[#F5F5F4]/50">Phone</label>
                    <input required type="tel" value={form.phone} onChange={set("phone")} placeholder="+91" className={inputCls} data-testid="enquiry-phone-input" />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold uppercase tracking-[0.26em] text-[#F5F5F4]/50">Email</label>
                    <input required type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} data-testid="enquiry-email-input" />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold uppercase tracking-[0.26em] text-[#F5F5F4]/50">Interested In</label>
                    <select value={form.configuration} onChange={set("configuration")} className={`${inputCls} cursor-pointer`} data-testid="enquiry-config-select">
                      <option value="" className="text-[#1C1917]">Select a configuration</option>
                      {CONFIG_OPTIONS.map((c) => (
                        <option key={c} value={c} className="text-[#1C1917]">{c}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mt-8">
                  <label className="text-[10px] font-semibold uppercase tracking-[0.26em] text-[#F5F5F4]/50">Message (Optional)</label>
                  <textarea rows={3} value={form.message} onChange={set("message")} placeholder="Anything you'd like us to know" className={`${inputCls} resize-none`} data-testid="enquiry-message-input" />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="enquiry-submit-btn"
                  className="mt-12 w-full rounded-full bg-[#A98B5F] py-4 text-xs font-semibold uppercase tracking-[0.26em] text-[#022C22] transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60 sm:w-auto sm:px-14"
                >
                  {submitting ? "Sending…" : "Submit Enquiry"}
                </button>
                <p className="mt-6 text-[11px] leading-relaxed text-[#F5F5F4]/40">
                  By submitting, you authorize Embassy South Reserve to contact you via call, SMS,
                  WhatsApp, or email for project-related communication.
                </p>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
}
